import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  fetchTopoTables, fetchTopoInverters, fetchTopoStations, fetchTopoFieldZones, fetchTopoPiles, fetchTopoRoads,
  tablePolygon, modulePolygon, tablePiles, convexHull, padHull, circlePolygon, TABLE_COLS, TABLE_ROWS,
  type TopoTable, type TopoInverter, type TopoStation, type TopoFieldZone, type TopoPile, type TopoRoad,
} from "@/lib/topology";
import { useGrid } from "@/state/grid";
import { eng } from "@/lib/engineApi";
import { FieldDetailPanel } from "./FieldDetailPanel";

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || "";

// Layout (type) palette — used by the "Layout" view mode.
const TYPE_COLOR: Record<string, string> = { "3x27": "#2C9B73", "3x18": "#3A719E", "3x9": "#B8862B" };
// Inspection-status palette (brief §03 — all six states). We derive them from
// the engine's {status, source}: a manual manager signoff (source "manual") is
// the closed state (green pass / orange re-drive-pending fail); an auto-derived
// result (source "derived") is the open state awaiting closure (blue
// awaiting-sign-off pass / red failed-NCR).
const STATUS_COLOR: Record<string, string> = {
  none: "#8A9BB0", partial: "#F5A623", pass: "#00C48C", fail: "#E05252", redrive: "#F07030", signoff: "#4A9EFF",
};
const STATUS_LABEL: Record<string, string> = {
  none: "Not started", partial: "In progress", pass: "Passed", fail: "Failed / NCR", redrive: "Re-drive pending", signoff: "Awaiting sign-off",
};
const STATUS_ORDER = ["none", "partial", "pass", "signoff", "fail", "redrive"] as const;

type ViewMode = "status" | "layout";
type LayerKey = "fields" | "rows" | "tables" | "panels" | "piles" | "inverters" | "stations" | "roads" | "gps";
type LayerVis = Record<LayerKey, boolean>;

const LAYER_META: { key: LayerKey; label: string }[] = [
  { key: "fields", label: "Field boundaries" },
  { key: "rows", label: "Row numbers" },
  { key: "tables", label: "Table outlines" },
  { key: "panels", label: "Panel grid (z19+)" },
  { key: "piles", label: "Pile positions (z17+)" },
  { key: "inverters", label: "Inverters + numbers" },
  { key: "stations", label: "Transformer stations" },
  { key: "roads", label: "Internal roads" },
  { key: "gps", label: "My location (GPS)" },
];
const DEFAULT_VIS: LayerVis = { fields: true, rows: true, tables: true, panels: false, piles: false, inverters: true, stations: true, roads: true, gps: false };
const LS_VIS = "gp.map.layers";
const LS_MODE = "gp.map.viewmode";
const LS_EXC = "gp.map.exceptions";

const sKey = (f: number, t: number) => `${f}::${t}`;

/** Per-table effective inspection status → one of the six brief §03 states.
 *  Uses the engine's {status, source} (CLAUDE.md: never re-derive). */
function statusFor(field: number, table: number): string {
  const r = eng().tableEffectiveStatus(`Field ${field}`, table);
  if (r.status === "pass") return r.source === "manual" ? "pass" : "signoff";   // green vs blue
  if (r.status === "fail") return r.source === "manual" ? "redrive" : "fail";   // orange vs red
  if (r.status === "partial") return "partial";
  return "none";
}

// Issue #9 — open-NCR count per table. A red table tells you it failed but not
// how many open non-conformances are on it; the count badge does. We read the
// engine's punch pins (open, table-scope) and tally them per Field::table.
function buildNcrByTable(): Map<string, number> {
  const e = eng();
  const m = new Map<string, number>();
  try {
    for (const pin of e.punchPinsForMap({ showResolved: false }) as any[]) {
      const it = pin.item;
      if (pin.anchor !== "field" || it.workScope === "station" || it.workScope === "site") continue;
      const fm = /Field\s+(\d+)/.exec(String(it.fid));
      if (!fm) continue;
      const k = sKey(Number(fm[1]), it.t);
      m.set(k, (m.get(k) || 0) + 1);
    }
  } catch { /* engine offline / no pins */ }
  return m;
}

function buildTablesFC(tables: TopoTable[], status: Map<string, string>, ncr?: Map<string, number>): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: tables.map((t) => {
      const key = sKey(t.field_no, t.table_no);
      return {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [tablePolygon(t)] },
        properties: {
          field: t.field_no, table: t.table_no, ttype: t.type,
          status: status.get(key) ?? "none",
          ncr: ncr?.get(key) ?? 0,
          // CAD row number (migration 0024); 0 when topology predates the column.
          row: t.row_no ?? 0,
        },
      };
    }),
  };
}

// Issue #10 — "Show only exceptions": hide accepted/not-started tables, keeping
// the states a manager actually needs to chase. Used as a Mapbox filter expr.
const EXCEPTION_STATES = ["fail", "redrive", "partial", "signoff"];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const exceptionsFilter: any = ["match", ["get", "status"], EXCEPTION_STATES, true, false];

const overallToStatus = (o: string): string => (o === "pass" ? "pass" : o === "fail" ? "fail" : o === "partial" ? "partial" : "none");

// The real ZIM pile set (migration 0013) covers ~19k piles across all fields;
// the partial legacy set (0010) is only ~4.2k. Prefer real geometry once the
// full set is present, else fall back to piles derived from table geometry.
const REAL_PILES_MIN = 12000;

/** Pile points (brief §02.6: pile circles per table row), coloured by per-pile
 *  QC result (pileRoll). Uses the REAL ZIM survey positions when the full set is
 *  loaded; otherwise derives a complete per-table layout from table geometry.
 *  Pile index matches the engine's pkey scheme either way, so the colour
 *  reflects the same pile QC inspects. */
function buildPilesFC(tables: TopoTable[], real: TopoPile[]): GeoJSON.FeatureCollection {
  const e = eng();
  const features: GeoJSON.Feature[] = [];
  if (real.length >= REAL_PILES_MIN) {
    for (const p of real) {
      if (!isFinite(p.lng) || !isFinite(p.lat)) continue;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: {
          field: p.field_no, table: p.table_no, idx: p.pile_idx, method: p.method, depth: p.depth_m,
          status: overallToStatus(e.pileRoll(`Field ${p.field_no}`, p.table_no, p.pile_idx).overall),
        },
      });
    }
    return { type: "FeatureCollection", features };
  }
  for (const t of tables) {
    const fid = `Field ${t.field_no}`;
    for (const p of tablePiles(t)) {
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.lng, p.lat] },
        properties: { field: t.field_no, table: t.table_no, idx: p.idx, status: overallToStatus(e.pileRoll(fid, t.table_no, p.idx).overall) },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

/** Per-module panel cells for ALL tables (brief §02.5 panel grid), coloured by
 *  per-module QC result (moduleRoll). Heavy (~71k cells) — built lazily, z19+. */
function buildPanelsFC(tables: TopoTable[]): GeoJSON.FeatureCollection {
  const e = eng();
  const features: GeoJSON.Feature[] = [];
  for (const t of tables) {
    const fid = `Field ${t.field_no}`;
    const cells = (TABLE_COLS[t.type] ?? TABLE_COLS["3x27"]) * TABLE_ROWS;
    for (let m = 0; m < cells; m++) {
      features.push({
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [modulePolygon(t, m)] },
        properties: { field: t.field_no, table: t.table_no, m, status: overallToStatus(e.moduleRoll(fid, t.table_no, m).overall) },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

// A field's boundary polygon is fixed (it only depends on table positions), so
// the expensive convex-hull pass is done ONCE in buildFieldHulls; per-version
// refreshes (fieldsFCFromHulls) only recompute the cheap completion %.
interface FieldHull { fno: number; ring: [number, number][]; tableKeys: string[]; }

function buildFieldHulls(tables: TopoTable[], zones: TopoFieldZone[] = []): FieldHull[] {
  const byField = new Map<number, TopoTable[]>();
  for (const t of tables) { let a = byField.get(t.field_no); if (!a) byField.set(t.field_no, (a = [])); a.push(t); }
  const zoneByField = new Map<number, [number, number][]>(zones.map((z) => [z.field_no, z.ring]));
  const out: FieldHull[] = [];
  for (const [fno, fts] of [...byField.entries()].sort((a, b) => a[0] - b[0])) {
    const tableKeys = fts.map((t) => sKey(t.field_no, t.table_no));
    // Prefer the real DXF zone polygon (migration 0012); fall back to a padded
    // convex hull of the field's table corners.
    const zone = zoneByField.get(fno);
    if (zone && zone.length >= 4) { out.push({ fno, ring: zone, tableKeys }); continue; }
    const pts: [number, number][] = [];
    for (const t of fts) for (const c of tablePolygon(t)) pts.push(c);
    const hull = padHull(convexHull(pts), 12);
    if (hull.length < 3) continue;
    if (hull[0][0] !== hull[hull.length - 1][0] || hull[0][1] !== hull[hull.length - 1][1]) hull.push(hull[0]);
    out.push({ fno, ring: hull, tableKeys });
  }
  return out;
}

function fieldsFCFromHulls(hulls: FieldHull[], status: Map<string, string>): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: hulls.map((h) => {
      let done = 0;
      for (const k of h.tableKeys) { const s = status.get(k); if (s === "pass" || s === "fail") done++; }
      const pct = h.tableKeys.length ? Math.round((done / h.tableKeys.length) * 100) : 0;
      return {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [h.ring] },
        properties: { name: `Field ${h.fno}`, pct, label: `Field ${h.fno} · ${pct}%` },
      };
    }),
  };
}

function siteBoundaryFC(tables: TopoTable[]): GeoJSON.FeatureCollection {
  const pts: [number, number][] = [];
  for (const t of tables) for (const c of tablePolygon(t)) pts.push(c);
  const hull = padHull(convexHull(pts), 35);
  if (hull.length >= 3 && (hull[0][0] !== hull[hull.length - 1][0] || hull[0][1] !== hull[hull.length - 1][1])) hull.push(hull[0]);
  return { type: "FeatureCollection", features: hull.length >= 4 ? [{ type: "Feature", geometry: { type: "Polygon", coordinates: [hull] }, properties: {} }] : [] };
}

// Issue #11 — Row numbers. We now label the REAL CAD row number per table
// (topo_tables.row_no, from the "PVcase Row Numbering" F.R labels — migration
// 0024) so the map matches the engineering drawings. One "R{n}" label is placed
// at the centroid of each (field, row_no) group. When row_no is absent (old
// topology), fall back to the engine's row grouping so the layer never empties.
function buildRowsFC(tables: TopoTable[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  const hasCad = tables.some((t) => t.row_no != null);
  if (hasCad) {
    // group by field::row_no → centroid of insertion points
    const groups = new Map<string, { fno: number; row: number; sx: number; sy: number; n: number }>();
    for (const t of tables) {
      if (t.row_no == null || !isFinite(t.lng) || !isFinite(t.lat)) continue;
      const k = `${t.field_no}::${t.row_no}`;
      const g = groups.get(k) ?? { fno: t.field_no, row: t.row_no, sx: 0, sy: 0, n: 0 };
      g.sx += t.lng; g.sy += t.lat; g.n++;
      groups.set(k, g);
    }
    for (const g of groups.values()) {
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [g.sx / g.n, g.sy / g.n] },
        properties: { field: g.fno, row: g.row, label: `R${g.row}` },
      });
    }
    return { type: "FeatureCollection", features };
  }
  // Fallback: engine row grouping (rowsForField / tablesInRow — not re-derived).
  const e = eng();
  const pos = new Map<string, TopoTable>();
  for (const t of tables) pos.set(sKey(t.field_no, t.table_no), t);
  for (const f of e.D.fields as any[]) {
    const m = /Field\s+(\d+)/.exec(f.id);
    if (!m) continue;
    const fno = Number(m[1]);
    const nRows = e.rowsForField(f);
    for (let row = 1; row <= nRows; row++) {
      let sx = 0, sy = 0, n = 0;
      for (const tno of e.tablesInRow(f, row) as number[]) {
        const t = pos.get(sKey(fno, tno));
        if (!t || !isFinite(t.lng) || !isFinite(t.lat)) continue;
        sx += t.lng; sy += t.lat; n++;
      }
      if (!n) continue;
      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [sx / n, sy / n] },
        properties: { field: fno, row, label: `R${row}` },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

// Issue #9 — Internal roads. Empty until road geometry is ingested (see
// fetchTopoRoads); the layer is wired so it lights up automatically when present.
function buildRoadsFC(roads: TopoRoad[]): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: roads.map((r) => ({ type: "Feature", geometry: { type: "LineString", coordinates: r.path }, properties: { kind: r.kind } })),
  };
}

const fillColorExpr = (mode: ViewMode): mapboxgl.ExpressionSpecification =>
  (mode === "status"
    ? ["match", ["get", "status"],
        "pass", STATUS_COLOR.pass, "signoff", STATUS_COLOR.signoff, "partial", STATUS_COLOR.partial,
        "fail", STATUS_COLOR.fail, "redrive", STATUS_COLOR.redrive, STATUS_COLOR.none]
    : ["match", ["get", "ttype"], "3x27", TYPE_COLOR["3x27"], "3x18", TYPE_COLOR["3x18"], "3x9", TYPE_COLOR["3x9"], "#888"]) as mapboxgl.ExpressionSpecification;

// localStorage can throw (Safari private mode, sandboxed iframes) — never let
// a persistence read/write crash the map.
function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, val: string): void {
  try { localStorage.setItem(key, val); } catch { /* ignore */ }
}
function loadVis(): LayerVis {
  try { const v = JSON.parse(lsGet(LS_VIS) || "null"); if (v) return { ...DEFAULT_VIS, ...v }; } catch { /* ignore */ }
  return DEFAULT_VIS;
}
function loadMode(): ViewMode {
  return lsGet(LS_MODE) === "layout" ? "layout" : "status";
}

/** Geographic Site Map — full spatial layer stack (brief v2): satellite base,
 *  site boundary, field zones, table rectangles coloured by inspection status,
 *  piles, inverters, transformer stations, GPS blue dot. Layer toggles + a
 *  Layout↔Inspection view switch + a tap-to-inspect drawer. */
export default function MapboxSiteMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const tablesRef = useRef<TopoTable[]>([]);
  const pilesRef = useRef<TopoPile[]>([]);
  const fieldHullsRef = useRef<FieldHull[]>([]);
  const watchRef = useRef<number | null>(null);
  const readyRef = useRef(false);
  const version = useGrid((s) => s.version);

  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [msg, setMsg] = useState("");
  const [counts, setCounts] = useState({ tables: 0, inverters: 0, stations: 0 });
  const [mode, setMode] = useState<ViewMode>(loadMode);
  const [vis, setVis] = useState<LayerVis>(loadVis);
  const [exceptionsOnly, setExceptionsOnly] = useState<boolean>(() => lsGet(LS_EXC) === "1");
  const [gpsErr, setGpsErr] = useState("");
  // UI brief #2: the layer panel is collapsed by default and opened via a floating
  // "Layers" button, so the map loads full-width.
  const [panelOpen, setPanelOpen] = useState(false);

  // ---- init map + data (once) ---------------------------------------------
  useEffect(() => {
    if (!TOKEN) { setStatus("error"); setMsg("Set VITE_MAPBOX_TOKEN in .env to enable the geographic map."); return; }
    let map: mapboxgl.Map | null = null;
    let cancelled = false;

    (async () => {
      let tables: TopoTable[] = [], inverters: TopoInverter[] = [], stations: TopoStation[] = [], zones: TopoFieldZone[] = [], realPiles: TopoPile[] = [], roads: TopoRoad[] = [];
      try {
        [tables, inverters, stations, zones, realPiles, roads] = await Promise.all([
          fetchTopoTables(), fetchTopoInverters(), fetchTopoStations(), fetchTopoFieldZones(), fetchTopoPiles(), fetchTopoRoads(),
        ]);
      } catch (e) { setStatus("error"); setMsg(e instanceof Error ? e.message : "Failed to load topology"); return; }
      if (cancelled || !containerRef.current) return;
      if (!tables.length) { setStatus("error"); setMsg("No topology found. Import the field DXFs first."); return; }
      tablesRef.current = tables;
      pilesRef.current = realPiles;
      setCounts({ tables: tables.length, inverters: inverters.length, stations: stations.length });

      const statusMap = new Map<string, string>();
      for (const t of tables) statusMap.set(sKey(t.field_no, t.table_no), statusFor(t.field_no, t.table_no));
      const ncrMap = buildNcrByTable();

      mapboxgl.accessToken = TOKEN;
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: "mapbox://styles/mapbox/satellite-streets-v12",
        center: [tables[0].lng, tables[0].lat], zoom: 14, attributionControl: false,
        // Issue #7: required so the WebGL canvas can be read back into an image
        // for the PDF/print export (otherwise toDataURL returns a blank frame).
        preserveDrawingBuffer: true,
      });
      mapRef.current = map;
      map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), "top-right");
      map.addControl(new mapboxgl.ScaleControl({ unit: "metric" }), "bottom-left");

      const tablesFC = buildTablesFC(tables, statusMap, ncrMap);
      fieldHullsRef.current = buildFieldHulls(tables, zones);
      const fieldsFC = fieldsFCFromHulls(fieldHullsRef.current, statusMap);
      const boundaryFC = siteBoundaryFC(tables);
      const rowsFC = buildRowsFC(tables);
      const roadsFC = buildRoadsFC(roads);
      // Issue #12 — label inverters with the REAL CAD designator (topo_inverters
      // .label e.g. "TR7-INV1", migration 0025) when present; else the sequential
      // 1-based number ("I{n}") as a fallback for un-ingested topology.
      const invFC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: inverters.map((i) => ({ type: "Feature", geometry: { type: "Point", coordinates: [i.lng, i.lat] }, properties: { field: i.field_no, idx: i.idx, num: i.idx + 1, label: i.label ?? null, disp: i.label ?? `I${i.idx + 1}` } })) };
      const stFC: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: stations.map((s) => ({ type: "Feature", geometry: { type: "Point", coordinates: [s.lng, s.lat] }, properties: { id: s.id, name: s.name, mva: s.rating_mva } })) };
      // piles + panel grid are heavy & opt-in → sources start empty, filled lazily on toggle.
      const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

      map.on("load", () => {
       try {
        if (!map) return;
        const v0 = loadVis();
        const vv = (k: LayerKey): "visible" | "none" => (v0[k] ? "visible" : "none");

        // 2 — site boundary / fence (derived from table extents)
        map.addSource("site-boundary", { type: "geojson", data: boundaryFC });
        map.addLayer({ id: "site-boundary", type: "line", source: "site-boundary", paint: { "line-color": "#FFFFFF", "line-width": 2.4, "line-opacity": 0.9, "line-dasharray": [3, 2] } });

        // 2b — internal roads (issue #9; empty until road geometry is ingested)
        map.addSource("roads", { type: "geojson", data: roadsFC });
        map.addLayer({ id: "roads", type: "line", source: "roads", layout: { visibility: vv("roads"), "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#D9A441", "line-width": ["interpolate", ["linear"], ["zoom"], 13, 1.5, 18, 6], "line-opacity": 0.85 } });

        // 3 — field boundaries (zoom 13+) + labels
        map.addSource("fields", { type: "geojson", data: fieldsFC });
        map.addLayer({ id: "fields-fill", type: "fill", source: "fields", minzoom: 13, layout: { visibility: vv("fields") }, paint: { "fill-color": "#4A9EFF", "fill-opacity": 0.05 } });
        map.addLayer({ id: "fields-line", type: "line", source: "fields", minzoom: 13, layout: { visibility: vv("fields") }, paint: { "line-color": "#4A9EFF", "line-width": 1.6, "line-opacity": 0.85 } });
        map.addLayer({ id: "fields-label", type: "symbol", source: "fields", minzoom: 13, layout: { visibility: vv("fields"), "text-field": ["get", "label"], "text-size": 13, "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"] }, paint: { "text-color": "#EAF2FF", "text-halo-color": "#0B1A2E", "text-halo-width": 1.6 } });

        // 3b — row numbers (issue #5): the QC navigator's rows, labelled on the map.
        map.addSource("rows", { type: "geojson", data: rowsFC });
        map.addLayer({ id: "rows-label", type: "symbol", source: "rows", minzoom: 15, layout: { visibility: vv("rows"), "text-field": ["get", "label"], "text-size": ["interpolate", ["linear"], ["zoom"], 15, 10, 19, 15], "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"], "text-allow-overlap": false }, paint: { "text-color": "#FFE9A8", "text-halo-color": "#241A00", "text-halo-width": 1.5 } });

        // 4 — table outlines, coloured by inspection status (zoom 14+)
        map.addSource("tables", { type: "geojson", data: tablesFC });
        map.addLayer({ id: "tables-fill", type: "fill", source: "tables", minzoom: 14, layout: { visibility: vv("tables") }, paint: { "fill-color": fillColorExpr(loadMode()), "fill-opacity": ["interpolate", ["linear"], ["zoom"], 14, 0.55, 18, 0.82] } });
        map.addLayer({ id: "tables-line", type: "line", source: "tables", minzoom: 16, layout: { visibility: vv("tables") }, paint: { "line-color": "#0E141B", "line-width": 0.6, "line-opacity": 0.7 } });
        map.addLayer({ id: "tables-label", type: "symbol", source: "tables", minzoom: 18.5, layout: { visibility: vv("tables"), "text-field": ["to-string", ["get", "table"]], "text-size": 10, "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"] }, paint: { "text-color": "#fff", "text-halo-color": "#000", "text-halo-width": 1 } });
        // 4b — open-NCR count badge (issue #9): only on tables with ≥1 open NCR,
        // visible from z15 so failures stand out before the per-table label.
        map.addLayer({ id: "tables-ncr", type: "symbol", source: "tables", minzoom: 15, filter: [">", ["get", "ncr"], 0], layout: { visibility: vv("tables"), "text-field": ["concat", "✕", ["to-string", ["get", "ncr"]]], "text-size": ["interpolate", ["linear"], ["zoom"], 15, 11, 19, 16], "text-font": ["DIN Pro Bold", "Arial Unicode MS Bold"], "text-allow-overlap": true, "text-ignore-placement": true }, paint: { "text-color": "#fff", "text-halo-color": "#B8281F", "text-halo-width": 2.2 } });
        // Low-zoom proxy so tables read as dots before the fill kicks in.
        map.addLayer({ id: "tables-dot", type: "circle", source: "tables", maxzoom: 14, layout: { visibility: vv("tables") }, paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 11, 1.5, 14, 3.5], "circle-color": fillColorExpr(loadMode()), "circle-opacity": 0.9 } });

        // 5 — panel grid (zoom 19+): per-module cells coloured by module QC result.
        map.addSource("panels", { type: "geojson", data: EMPTY });
        map.addLayer({ id: "panels-fill", type: "fill", source: "panels", minzoom: 19, layout: { visibility: vv("panels") }, paint: {
          "fill-color": ["match", ["get", "status"], "pass", STATUS_COLOR.pass, "fail", STATUS_COLOR.fail, "partial", STATUS_COLOR.partial, "rgba(150,170,190,0.25)"],
          "fill-opacity": 0.7, "fill-outline-color": "#0E141B",
        } });

        // 6 — piles (zoom 17+), coloured by pile inspection result (brief §02.6)
        map.addSource("piles", { type: "geojson", data: EMPTY });
        map.addLayer({ id: "piles", type: "circle", source: "piles", minzoom: 17, layout: { visibility: vv("piles") }, paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 17, 1.4, 20, 4],
          "circle-color": ["match", ["get", "status"], "pass", STATUS_COLOR.pass, "fail", STATUS_COLOR.fail, "partial", STATUS_COLOR.partial, "#8FB7D6"],
          "circle-stroke-width": 0.4, "circle-stroke-color": "#0E141B",
        } });

        // inverters + their numbers (issue #6): number always shown from z15.5,
        // and a click popup gives field + inverter number at any zoom.
        map.addSource("inverters", { type: "geojson", data: invFC });
        map.addLayer({ id: "inverters", type: "circle", source: "inverters", layout: { visibility: vv("inverters") }, paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 13, 2, 18, 6], "circle-color": "#E3B23C", "circle-stroke-width": 1, "circle-stroke-color": "#1a1206" } });
        map.addLayer({ id: "inverter-labels", type: "symbol", source: "inverters", minzoom: 15.5, layout: { visibility: vv("inverters"), "text-field": ["get", "disp"], "text-size": ["interpolate", ["linear"], ["zoom"], 15.5, 9, 19, 13], "text-offset": [0, 1.1], "text-anchor": "top", "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"], "text-allow-overlap": false }, paint: { "text-color": "#FFE08A", "text-halo-color": "#2A1C00", "text-halo-width": 1.3 } });

        // transformer stations + labels
        map.addSource("stations", { type: "geojson", data: stFC });
        map.addLayer({ id: "stations", type: "circle", source: "stations", layout: { visibility: vv("stations") }, paint: { "circle-radius": ["interpolate", ["linear"], ["zoom"], 12, 5, 18, 12], "circle-color": "#9B5DE5", "circle-stroke-width": 2, "circle-stroke-color": "#fff" } });
        map.addLayer({ id: "station-labels", type: "symbol", source: "stations", layout: { visibility: vv("stations"), "text-field": ["concat", ["get", "name"], "  ", ["to-string", ["get", "mva"]], " MVA"], "text-size": 11, "text-offset": [0, 1.3], "text-anchor": "top", "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"] }, paint: { "text-color": "#fff", "text-halo-color": "#1a0d33", "text-halo-width": 1.4 } });

        // 8 — GPS blue dot (accuracy ring + dot) — empty until a fix arrives
        const empty: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
        map.addSource("gps-acc", { type: "geojson", data: empty });
        map.addSource("gps-dot", { type: "geojson", data: empty });
        map.addLayer({ id: "gps-acc", type: "fill", source: "gps-acc", layout: { visibility: vv("gps") }, paint: { "fill-color": "#4A9EFF", "fill-opacity": 0.12 } });
        map.addLayer({ id: "gps-dot", type: "circle", source: "gps-dot", layout: { visibility: vv("gps") }, paint: { "circle-radius": 7, "circle-color": "#4A9EFF", "circle-stroke-width": 3, "circle-stroke-color": "#fff" } });

        // fit to all tables
        const b = new mapboxgl.LngLatBounds();
        tables.forEach((t) => b.extend([t.lng, t.lat]));
        map.fitBounds(b, { padding: 60, duration: 0 });

        // interactions
        const cur = (on: boolean) => map && (map.getCanvas().style.cursor = on ? "pointer" : "");
        for (const lyr of ["tables-fill", "tables-dot", "panels-fill", "fields-fill", "stations", "piles"]) {
          map.on("mouseenter", lyr, () => cur(true));
          map.on("mouseleave", lyr, () => cur(false));
        }
        // One click handler so a table/panel click (which also overlaps its field
        // polygon) selects the table, not the field underneath it. Selection
        // drives the shared FieldDetailPanel via the engine's selField/selTable.
        const eg = eng();
        map.on("click", (ev) => {
          const hits = map!.queryRenderedFeatures(ev.point, { layers: ["panels-fill", "tables-fill", "tables-dot", "fields-fill"] });
          const tbl = hits.find((h) => ["panels-fill", "tables-fill", "tables-dot"].includes(h.layer!.id));
          if (tbl) {
            const p = tbl.properties as any;
            eg.state.selField = `Field ${p.field}`; eg.state.selTable = p.table; useGrid.getState().bump();
            return;
          }
          const fld = hits.find((h) => h.layer!.id === "fields-fill");
          if (fld) {
            eg.state.selField = (fld.properties as any).name; eg.state.selTable = null; useGrid.getState().bump();
          }
        });
        map.on("click", "stations", (ev) => {
          const f = ev.features?.[0]; if (!f) return; const p = f.properties as any;
          new mapboxgl.Popup({ offset: 12 }).setLngLat((f.geometry as any).coordinates)
            .setHTML(`<div class="map-pop"><div class="mp-h">${p.name}</div><div class="mp-t" style="color:#9B5DE5">Transformer · ${p.mva} MVA</div></div>`).addTo(map!);
        });
        map.on("click", "inverters", (ev) => {
          const f = ev.features?.[0]; if (!f) return; const p = f.properties as any;
          new mapboxgl.Popup({ offset: 10 }).setLngLat((f.geometry as any).coordinates)
            .setHTML(`<div class="map-pop"><div class="mp-h">${p.label ? p.label : "Inverter " + p.num}</div><div class="mp-t" style="color:#C8901E">Field ${p.field} · string inverter</div></div>`).addTo(map!);
        });
        map.on("mouseenter", "inverters", () => cur(true));
        map.on("mouseleave", "inverters", () => cur(false));
        map.on("click", "piles", (ev) => {
          const f = ev.features?.[0]; if (!f) return; const p = f.properties as any;
          const lbl = STATUS_LABEL[p.status] ?? "Not started";
          const meta = p.method ? `${p.method}${p.depth ? " · " + p.depth + " m" : ""} · ` : "";
          new mapboxgl.Popup({ offset: 8 }).setLngLat((f.geometry as any).coordinates)
            .setHTML(`<div class="map-pop"><div class="mp-h">Field ${p.field} · T${p.table} · Pile ${p.idx}</div><div class="mp-t" style="color:${STATUS_COLOR[p.status] ?? STATUS_COLOR.none}">${meta}${lbl}</div></div>`).addTo(map!);
        });

        readyRef.current = true;
        setStatus("ready");
       } catch (err) {
        setStatus("error");
        setMsg(err instanceof Error ? err.message : "Failed to build the map layers.");
       }
      });
      // Only surface fatal load errors (bad token, style fetch) — ignore the
      // transient per-tile errors that fire after the map is up.
      map.on("error", (e) => { if (!readyRef.current) { setStatus("error"); setMsg(e?.error?.message || "Mapbox failed to load (check the token)."); } });
    })();

    return () => { cancelled = true; if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } map?.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- re-derive inspection status on engine mutations --------------------
  useEffect(() => {
    if (status !== "ready") return;
    const map = mapRef.current; if (!map) return;
    const tables = tablesRef.current;
    const statusMap = new Map<string, string>();
    for (const t of tables) statusMap.set(sKey(t.field_no, t.table_no), statusFor(t.field_no, t.table_no));
    (map.getSource("tables") as mapboxgl.GeoJSONSource | undefined)?.setData(buildTablesFC(tables, statusMap, buildNcrByTable()));
    (map.getSource("fields") as mapboxgl.GeoJSONSource | undefined)?.setData(fieldsFCFromHulls(fieldHullsRef.current, statusMap));
    // Heavy, opt-in layers are derived from table geometry + QC rolls — only
    // (re)built while visible (lazily on first toggle, and on each QC change).
    if (vis.piles) (map.getSource("piles") as mapboxgl.GeoJSONSource | undefined)?.setData(buildPilesFC(tables, pilesRef.current));
    if (vis.panels) (map.getSource("panels") as mapboxgl.GeoJSONSource | undefined)?.setData(buildPanelsFC(tables));
  }, [version, status, vis.piles, vis.panels]);

  // ---- view mode (status ↔ layout) ----------------------------------------
  useEffect(() => {
    if (status !== "ready") return;
    const map = mapRef.current; if (!map) return;
    lsSet(LS_MODE, mode);
    map.setPaintProperty("tables-fill", "fill-color", fillColorExpr(mode));
    map.setPaintProperty("tables-dot", "circle-color", fillColorExpr(mode));
  }, [mode, status]);

  // ---- layer visibility ----------------------------------------------------
  useEffect(() => {
    if (status !== "ready") return;
    const map = mapRef.current; if (!map) return;
    lsSet(LS_VIS, JSON.stringify(vis));
    const set = (id: string, on: boolean) => map.getLayer(id) && map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
    set("fields-fill", vis.fields); set("fields-line", vis.fields); set("fields-label", vis.fields);
    set("rows-label", vis.rows);
    set("tables-fill", vis.tables); set("tables-line", vis.tables); set("tables-label", vis.tables); set("tables-dot", vis.tables); set("tables-ncr", vis.tables);
    set("panels-fill", vis.panels);
    set("piles", vis.piles);
    set("inverters", vis.inverters); set("inverter-labels", vis.inverters);
    set("stations", vis.stations); set("station-labels", vis.stations);
    set("roads", vis.roads);
    set("gps-acc", vis.gps); set("gps-dot", vis.gps);
  }, [vis, status]);

  // ---- "Show only exceptions" filter (issue #10) --------------------------
  useEffect(() => {
    if (status !== "ready") return;
    const map = mapRef.current; if (!map) return;
    lsSet(LS_EXC, exceptionsOnly ? "1" : "0");
    const f = exceptionsOnly ? exceptionsFilter : null;
    for (const id of ["tables-fill", "tables-line", "tables-label", "tables-dot"]) {
      if (map.getLayer(id)) map.setFilter(id, f);
    }
  }, [exceptionsOnly, status]);

  // ---- GPS watch (only while the GPS layer is on) -------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (status !== "ready" || !map) return;
    if (!vis.gps) {
      if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; }
      return;
    }
    if (!("geolocation" in navigator)) { setGpsErr("Geolocation unavailable"); return; }
    setGpsErr("");
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { longitude: lng, latitude: lat, accuracy } = pos.coords;
        (map.getSource("gps-dot") as mapboxgl.GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "Point", coordinates: [lng, lat] }, properties: {} }] });
        (map.getSource("gps-acc") as mapboxgl.GeoJSONSource | undefined)?.setData({ type: "FeatureCollection", features: [{ type: "Feature", geometry: { type: "Polygon", coordinates: [circlePolygon(lng, lat, Math.max(accuracy || 8, 4))] }, properties: {} }] });
      },
      (err) => setGpsErr(err.message || "Location permission denied"),
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 10000 },
    );
    return () => { if (watchRef.current != null) { navigator.geolocation.clearWatch(watchRef.current); watchRef.current = null; } };
  }, [vis.gps, status]);

  const openInspection = (fid: string, table: number) => {
    const e = eng();
    const f = e.D.fields.find((x: any) => x.id === fid) || e.D.fields[0];
    // QC is organised Row→Table and resets qcTable if it isn't in qcRow, so we
    // must point qcRow at the row that actually contains the tapped table.
    let row = 1;
    for (let r = 1; r <= e.rowsForField(f); r++) { if (e.tablesInRow(f, r).includes(table)) { row = r; break; } }
    e.state.qcWorkScope = "table"; e.state.qcField = fid; e.state.qcRow = row; e.state.qcTable = table;
    e.state.qcItem = 0; e.state.qcModule = null; e.state.qcPile = null; e.state.showModules = false; e.state.showPiles = false;
    useGrid.getState().setView("qc");
  };

  // Issue #7 — export the current map view as a printable PDF: a snapshot of the
  // map canvas plus a per-field acceptance summary. Opens the browser print
  // dialog (Save as PDF) so it needs no extra dependency.
  const exportMapPdf = () => {
    const map = mapRef.current;
    if (!map) return;
    let img = "";
    try { img = map.getCanvas().toDataURL("image/png"); } catch { /* tainted canvas — fall back to summary only */ }

    const e = eng();
    const esc = (s: string) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
    let totPass = 0, totFail = 0, totTables = 0;
    const rows = (e.D.fields as any[]).map((f) => {
      let pass = 0, fail = 0;
      for (let t = 1; t <= f.tables; t++) {
        const s = e.tableEffectiveStatus(f.id, t).status;
        if (s === "pass") pass++; else if (s === "fail") fail++;
      }
      totPass += pass; totFail += fail; totTables += f.tables;
      const done = pass + fail;
      const pct = done ? Math.round((pass / done) * 100) : 0;
      return `<tr><td>${esc(f.id)}</td><td style="text-align:right">${f.tables}</td><td style="text-align:right;color:#1a8a5a">${pass}</td><td style="text-align:right;color:#c0392b">${fail}</td><td style="text-align:right">${done}/${f.tables}</td><td style="text-align:right">${pct}%</td></tr>`;
    }).join("");
    const proj = e.D?.project?.name || "Project";
    const win = window.open("", "_blank", "width=1100,height=850");
    if (!win) { e.toast?.("Allow pop-ups to export the map PDF", "warn"); return; }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(proj)} — Site Map</title>
      <style>
        body{font-family:'Helvetica Neue',Arial,sans-serif;color:#13212e;margin:28px;}
        h1{font-size:20px;margin:0 0 2px;} .sub{color:#5a6b7a;font-size:12px;margin-bottom:16px;}
        img{width:100%;border:1px solid #cbd5dd;border-radius:6px;margin-bottom:18px;}
        table{width:100%;border-collapse:collapse;font-size:12px;}
        th,td{border-bottom:1px solid #e1e7ec;padding:6px 8px;} th{text-align:right;color:#5a6b7a;font-weight:600;}
        th:first-child,td:first-child{text-align:left;}
        tfoot td{font-weight:700;border-top:2px solid #b9c4cd;}
        @media print{ body{margin:12mm;} }
      </style></head><body>
      <h1>${esc(proj)} — Site Map (${mode === "status" ? "Inspection status" : "Table layout"})</h1>
      <div class="sub">${counts.tables.toLocaleString()} tables · ${counts.inverters} inverters · ${counts.stations} stations</div>
      ${img ? `<img src="${img}" alt="Site map snapshot"/>` : `<div class="sub">(Map image unavailable in this browser — summary only.)</div>`}
      <table><thead><tr><th>Field</th><th>Tables</th><th>Accepted</th><th>Failed</th><th>Inspected</th><th>Acceptance</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td>Total</td><td style="text-align:right">${totTables}</td><td style="text-align:right">${totPass}</td><td style="text-align:right">${totFail}</td><td style="text-align:right">${totPass + totFail}/${totTables}</td><td style="text-align:right">${totPass + totFail ? Math.round((totPass / (totPass + totFail)) * 100) : 0}%</td></tr></tfoot>
      </table>
      <script>window.onload=function(){setTimeout(function(){window.print();},350);};<\/script>
      </body></html>`);
    win.document.close();
  };

  const legend = mode === "status"
    ? STATUS_ORDER.map((k) => ({ c: STATUS_COLOR[k], label: STATUS_LABEL[k] }))
    : (["3x27", "3x18", "3x9"] as const).map((k) => ({ c: TYPE_COLOR[k], label: `${k} portrait` }));

  return (
    <div className="mapview mapview-geo">
      <div className="map-wrap">
      <div ref={containerRef} className="map-canvas" />

      {status !== "ready" && (
        <div className="map-overlay">{status === "loading" ? "Loading site topology…" : <div style={{ maxWidth: 340, textAlign: "center" }}>{msg}</div>}</div>
      )}

      {status === "ready" && (
        <>
          {/* Floating Layers button (UI brief #2) — toggles the panel; hidden while open. */}
          {!panelOpen && (
            <button className="map-layers-fab" onClick={() => setPanelOpen(true)} title="Show layers & view options">
              <span className="lf-icon">▤</span> Layers
            </button>
          )}

          {/* View-mode switch + layer toggles — slides in from the left when open */}
          <div className={`map-panel ${panelOpen ? "open" : ""}`}>
            <div className="mp-head">
              <span>Layers &amp; view</span>
              <button className="mp-close" onClick={() => setPanelOpen(false)} title="Hide panel">✕</button>
            </div>
            <div className="mp-seg">
              <button className={mode === "status" ? "on" : ""} onClick={() => setMode("status")}>Inspection</button>
              <button className={mode === "layout" ? "on" : ""} onClick={() => setMode("layout")}>Layout</button>
            </div>
            <div className="mp-layers">
              {LAYER_META.map((l) => (
                <label key={l.key} className="mp-row">
                  <input type="checkbox" checked={vis[l.key]} onChange={(e) => setVis((v) => ({ ...v, [l.key]: e.target.checked }))} />
                  <span>{l.label}</span>
                </label>
              ))}
            </div>
            <button
              className={`mp-exc ${exceptionsOnly ? "on" : ""}`}
              onClick={() => setExceptionsOnly((v) => !v)}
              title="Hide accepted and not-started tables — show only failures, re-drives, in-progress and awaiting sign-off"
            >
              {exceptionsOnly ? "✓ " : ""}Show only exceptions
            </button>
            {vis.gps && gpsErr && <div className="mp-gpserr">{gpsErr}</div>}
            <button className="mp-export" onClick={exportMapPdf} title="Export the current map view + acceptance summary as a PDF">
              ⤓ Export PDF
            </button>
          </div>

          {/* Legend reflects the active view mode */}
          <div className="map-legend">
            <b>{mode === "status" ? "Inspection status" : "Table type"} · {counts.tables.toLocaleString()} tables</b>
            {legend.map((g) => (<div key={g.label}><i style={{ background: g.c }} />{g.label}</div>))}
            <div><i style={{ background: "#E3B23C", borderRadius: "50%" }} />{counts.inverters} inverters</div>
            <div><i style={{ background: "#9B5DE5", borderRadius: "50%" }} />{counts.stations} stations</div>
          </div>

        </>
      )}
      </div>

      {/* Right rail: field stats + table grid + accept/fail + open-inspection.
          Shared with the schematic SiteMapView; clicking a field/table on the
          map selects it here (engine selField/selTable). */}
      <FieldDetailPanel onOpenInspection={openInspection} />

      <style>{`
        /* +37px reclaimed from the collapsed header ribbon (UI brief #1). */
        .mapview-geo{ height:calc(100dvh - 95px); min-height:520px; }
        .map-wrap{ position:relative; width:100%; height:100%; min-height:0; overflow:hidden; }
        .map-canvas{ position:absolute; inset:0; }
        .map-overlay{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center;
          background:var(--bg); color:var(--dim); font-size:13px; z-index:2; padding:24px; }

        /* UI brief #2 — floating Layers button (collapsed default) + slide-in panel. */
        .map-layers-fab{ position:absolute; top:14px; left:14px; z-index:5; display:flex; align-items:center; gap:7px;
          padding:8px 13px; font-size:12.5px; font-weight:600; cursor:pointer; color:var(--ink);
          background:rgba(14,20,27,.9); border:1px solid var(--line); border-radius:9px; backdrop-filter:blur(6px);
          box-shadow:0 4px 14px rgba(0,0,0,.3); }
        .map-layers-fab:hover{ border-color:#2C9B73; }
        .map-layers-fab .lf-icon{ color:#2C9B73; font-size:14px; line-height:1; }
        html[data-theme="light"] .map-layers-fab{ background:rgba(255,255,255,.92); border-color:#c9d4dd; color:#16242f; }

        .map-panel{ position:absolute; top:14px; left:14px; z-index:6; width:184px; background:rgba(14,20,27,.9);
          border:1px solid var(--line); border-radius:10px; padding:10px; color:var(--ink); backdrop-filter:blur(6px); font-size:12px;
          transform:translateX(calc(-100% - 24px)); opacity:0; pointer-events:none; transition:transform .22s ease, opacity .22s ease; }
        .map-panel.open{ transform:none; opacity:1; pointer-events:auto; }
        .mp-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:9px; font-size:11px;
          font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--dim); }
        .mp-close{ background:transparent; border:none; color:var(--dim); cursor:pointer; font-size:13px; line-height:1; padding:2px 4px; }
        .mp-close:hover{ color:var(--ink); }
        .mp-seg{ display:flex; gap:0; border:1px solid var(--line); border-radius:7px; overflow:hidden; margin-bottom:9px; }
        .mp-seg button{ flex:1; padding:6px 4px; font-size:11px; font-weight:600; cursor:pointer; background:transparent; color:var(--dim); border:none; }
        .mp-seg button.on{ background:#2C9B73; color:#fff; }
        .mp-layers{ display:flex; flex-direction:column; gap:5px; }
        .mp-row{ display:flex; align-items:center; gap:8px; cursor:pointer; color:var(--ink); line-height:1.3; }
        .mp-row input{ accent-color:#2C9B73; width:14px; height:14px; }
        .mp-gpserr{ margin-top:8px; font-size:10px; color:#E05252; }

        .map-legend{ position:absolute; left:14px; bottom:34px; z-index:3; background:rgba(14,20,27,.86);
          border:1px solid var(--line); border-radius:8px; padding:10px 12px; font-size:11px; color:var(--ink); backdrop-filter:blur(4px); }
        .map-legend b{ display:block; margin-bottom:6px; font-size:11px; }
        .map-legend div{ display:flex; align-items:center; gap:7px; line-height:1.7; color:var(--dim); }
        .map-legend i{ width:11px; height:11px; border-radius:2px; display:inline-block; }

        .map-pop{ font-family:'IBM Plex Sans',system-ui,sans-serif; min-width:150px; }
        .map-pop .mp-h{ font-weight:700; font-size:13px; color:#0E141B; }
        .map-pop .mp-t{ font-size:11px; font-family:'IBM Plex Mono',monospace; margin:2px 0 0; color:#3a4a5a; }
        .mapboxgl-popup-content{ border-radius:8px; padding:10px 12px; }

        .mp-exc{ margin-top:9px; width:100%; padding:6px 8px; font-size:11px; font-weight:600; cursor:pointer;
          background:transparent; color:var(--dim); border:1px solid var(--line); border-radius:7px; }
        .mp-exc.on{ background:#E0525222; color:#E05252; border-color:#E0525266; }
        .mp-export{ margin-top:9px; width:100%; padding:7px 8px; font-size:11.5px; font-weight:600; cursor:pointer;
          background:#2C9B73; color:#fff; border:none; border-radius:7px; }
        .mp-export:hover{ background:#258063; }
        html[data-theme="light"] .mp-exc{ color:#5a6b7a; border-color:#c9d4dd; }
        html[data-theme="light"] .mp-exc.on{ background:#E0525218; color:#c0392b; border-color:#E0525255; }

        /* Issue #3 — Light mode: the map panel/legend used a fixed dark glass that
           became dark-text-on-dark in the light theme (unreadable). Give them a
           light glass with dark text and a readable legend label colour. */
        html[data-theme="light"] .map-panel{ background:rgba(255,255,255,.92); border-color:#c9d4dd; color:#16242f; }
        html[data-theme="light"] .map-panel .mp-row{ color:#16242f; }
        html[data-theme="light"] .mp-seg{ border-color:#c9d4dd; }
        html[data-theme="light"] .mp-seg button{ color:#5a6b7a; }
        html[data-theme="light"] .mp-seg button.on{ background:#2C9B73; color:#fff; }
        html[data-theme="light"] .map-legend{ background:rgba(255,255,255,.93); border-color:#c9d4dd; color:#16242f; }
        html[data-theme="light"] .map-legend b{ color:#16242f; }
        html[data-theme="light"] .map-legend div{ color:#3a4a5a; }
        html[data-theme="light"] .map-legend i{ box-shadow:0 0 0 1px rgba(0,0,0,.12); }
      `}</style>
    </div>
  );
}
